// Angular imports
import {
    Component,
    OnInit,
    Input,
    ViewChild,
    Output,
    EventEmitter,
    SimpleChanges,
    OnDestroy,
    OnChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

// 3rd party imports
import { Subscription } from 'rxjs';
import * as moment from 'moment-timezone';

// devextreme imports
import notify from 'devextreme/ui/notify';
import { custom } from 'devextreme/ui/dialog';
import {
    DxDataGridComponent,
    DxSelectBoxComponent,
    DxDateBoxComponent,
    DxTextBoxComponent,
    DxScrollViewComponent
} from 'devextreme-angular';
import { formatNumber } from 'devextreme/localization';

// CC-Common imports
import {
    IconConstants,
    MessageBoxComponent,
    CCMessageBoxIcon,
    CCLocalStorageService,
    CCLocalStorageCategory,
    Utils
} from 'cc-common';

// Component imports
import { MultiColumnSelectorComponent } from '../../../management-common/component/common/multi-column-selector/multi-column-selector.component';
import { ManageVendorAccountsComponent } from '../../../vendor/components/manage-vendor-accounts/manage-vendor-accounts.component';

// Service imports
import { UserService } from '../../../_services/data-services/user.service';
import { WorkflowEditorService } from '../../_services/workflow-editor.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { FileReaderService } from '../../../_services/shared-services/file-reader.service';
import { CommunityService } from '../../../_services/data-services/community.service';
import { WorkflowEditorSharedService } from '../../../shared-workflow-module/_services/workflow-editor-shared.service';
import { WorkflowSharedService } from '../../../shared-workflow-module/_services/workflow-shared.service';
import { DetailPopupsService } from '../../../cirabooks/shared-cirabooks-module/_services/detail-popups.service';

// Model imports
import { WorkFlowParametersDto } from '../../../shared-workflow-module/_models/workflow-parameters-dto';
import { WFESpecialHandlingResultDto } from '../../../shared-workflow-module/_models/wfe-special-handling-result-dto';
import { WFEWorkflowRawDto } from '../../../shared-workflow-module/_models/wfe-workflow-raw-dto';
import { WFEBaseParametersDto } from '../../../shared-workflow-module/_models/wfe-base-parameter-dto';
import { WFEVendorParametersDto } from '../../_models/common/wfe-vendor-parameter-dto';
import { WFEVendorAccountParametersDto } from '../../_models/workflow-data-entry/wfe-vendor-account-parameters-dto';
import { WFECreateCommunityVendorParametersDto } from '../../_models/workflow-data-entry/wfe-create-community-vendor-parameters-dto';
import { StatusMessageResultDto } from '../../../_models/status-message-result-dto';
import { WFEDeleteWorkflowParametersDto } from '../../_models/common/wfe-delete-workflow-parameters-dto';
import { WFEReplaceInvoiceFileParametersDto } from '../../_models/common/wfe-replace-invoice-file-parameters-dto';
import { AttachmentParameterDto } from '../../../_models/attachment-parameter-dto';
import { WFEWorkflowItemRawDto } from '../../_models/workflow-data-entry/wfe-workflow-item-raw-dto';
import { WFEVendorTemplateResultDto } from '../../_models/workflow-data-entry/wfe-vendor-template-result-dto';
import { WFEVendorAccountTemplateResultDto } from '../../_models/workflow-data-entry/wfe-vendor-account-template-result-dto';
import { WFESendToVendorEditParametersDto } from '../../_models/workflow-data-entry/wfe-send-to-vendor-edit-parameters-dto';
import { ManageVendorAccountsResultDto } from '../../../vendor/_models/manage-vendor-accounts-result-dto';
import { DropDownButtonMenuItemObject } from '../../_models/common/dropdown-button-menu-item-dto';
import { VendorAccountResultDto } from '../../../vendor/_models/vendor-account-result-dto';
import { WFVendorHistoryParametersDto } from '../../../shared-workflow-module/_models/wf-vendor-history-parameters-dto';
import { WFInvoiceHistoryParametersDto } from '../../../shared-workflow-module/_models/wf-invoice-history-parameters-dto';
import { WFEVendorAccountEFTActiveStatusParameterDto } from '../../../shared-workflow-module/_models/wfe-vendor-account-eft-active-status-parameter-dto';
import { WFECommunityResultDto } from '../../../shared-workflow-module/_models/wfe-community-result-dto';
import { WFEVendorResultDto } from '../../../shared-workflow-module/_models/wfe-vendor-result-dto';
import { WFEOwnerResultDto } from '../../../shared-workflow-module/_models/wfe-owner-result-dto';
import { VendorLineItemTemplateRawDto } from '../../../shared-workflow-module/_models/vendor-lineItem-template-raw-dto';
import { WFEVendorAccountResultDto } from '../../../shared-workflow-module/_models/wfe-vendor-account-result-dto';

// Constants imports
import { MessageInterval } from '../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { StorageKeyConstants } from '../../../_constants/storage-key-constants';
import { Constants } from '../../../_constants/constants';
import { ActionConstants } from '../../../_constants/action-constants';

@Component({
    selector: 'app-workflow-data-entry',
    templateUrl: './workflow-data-entry.component.html',
    styleUrls: ['./workflow-data-entry.component.scss']
})
export class WorkflowDataEntryComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('workflowDataGrid', { static: false }) workflowDataGrid: DxDataGridComponent;

    @ViewChild('specialHandling', { static: false }) specialHandling: DxSelectBoxComponent;
    @ViewChild('invoiceDate', { static: false }) invoiceDate: DxDateBoxComponent;
    @ViewChild('dueDate', { static: false }) dueDate: DxDateBoxComponent;
    @ViewChild('invoiceNumber', { static: false }) invoiceNumber: DxTextBoxComponent;

    @ViewChild('workflowMessageBox', { static: false }) workflowMessageBox: MessageBoxComponent;
    @ViewChild('communitySelector', { static: false }) communitySelector: MultiColumnSelectorComponent;
    @ViewChild('vendorPayeeSelector', { static: false }) vendorPayeeSelector: MultiColumnSelectorComponent;
    @ViewChild('ownerPayeeSelector', { static: false }) ownerPayeeSelector: MultiColumnSelectorComponent;
    @ViewChild('communityPayeeSelector', { static: false }) communityPayeeSelector: MultiColumnSelectorComponent;
    @ViewChild('vendorAccountSelector', { static: false }) vendorAccountSelector: MultiColumnSelectorComponent;
    @ViewChild('manageVendorAccount', { static: false }) manageVendorAccount: ManageVendorAccountsComponent;
    @ViewChild('workflowScrollView', { static: false }) workflowScrollView: DxScrollViewComponent;

    @Input() workflowId: number;
    @Input() communityId: number;
    @Input() isPopupMode = false;

    @Output() pdfURL = new EventEmitter<string>();
    @Output() pdfLoader = new EventEmitter<boolean>();
    @Output() closeParentContainer = new EventEmitter<boolean>();
    @Output() splitterPositionChange = new EventEmitter<boolean>();
    @Output() hideSplitter = new EventEmitter<boolean>();

    communityGridSource: WFECommunityResultDto[];
    selectedCommunity: WFECommunityResultDto;
    selectedVendorPayee: WFEVendorResultDto;
    selectedOwnerPayee: WFEOwnerResultDto;
    selectedCommunityPayee: WFECommunityResultDto;
    selectedVendorAccount: WFEVendorAccountResultDto;
    selectedSpecialHandling: WFESpecialHandlingResultDto;
    communityGridBoxValue: number[];
    wfItemsDataGridSource: WFEWorkflowItemRawDto[];
    specialHandlingSource: WFESpecialHandlingResultDto[];
    vendorAccountSource: WFEVendorAccountResultDto[];
    vendorHistoryBaseParams: WFVendorHistoryParametersDto;
    vendorAccountHistoryBaseParams: WFVendorHistoryParametersDto;
    invoiceHistoryBaseParams: WFInvoiceHistoryParametersDto;
    workflowHistoryBaseParams: WFEBaseParametersDto;
    accountEditGridSource: ManageVendorAccountsResultDto;
    workFlowEditor: WFEWorkflowRawDto;
    payeeVendorBoxGridSource: WFEVendorResultDto[];
    payeeVendorGridBoxValue: string[];
    payeeOwnerBoxGridSource: WFEOwnerResultDto[];
    payeeOwnerGridBoxValue: string[];
    payeeCommunityBoxGridSource: WFECommunityResultDto[];
    defaultLineItems: VendorLineItemTemplateRawDto[] = [];
    workflowOptions: DropDownButtonMenuItemObject[];
    selectedVendorAccountForEditor: VendorAccountResultDto;
    vendorBillToAddress: string;
    vendorAccountBoxValue: number[];

    payeeCommunityGridBoxValue: string[];
    payeeOptions: any[];
    selectedPayee: string;
    selectedPayeeName: string;
    selectedPayeeCode: string;
    selectedVendorPayeeIndex: number;
    selectedVendorAccountId: number;
    vendorRemitAddress: string;
    addEditVendorAccountPopUpTitle: string;
    vendorAccountConfigMode: string;
    showAddEditVendorAccount = false;
    showWorkFlowNotesPopUp = false;
    showWorkFlowVendorInstructionsPopUp = false;
    showWorkFlowHistoryPopUp = false;
    showHistoryPopUp = false;
    showVendorNotesPopUp = false;
    showDeleteWorkflowPopUp = false;
    showProrateTaxChanges = false;
    showReplaceInvoiceFilePopUp = false;
    showManageVendorAccountPopup = false;
    isVendorInstructionsAvailable = false;
    isCommunitySelected = false;
    isVendorSelected = false;
    isInvoiceNumberEntered = false;
    isBranchVendor = false;
    loadingVisible = false;
    separateWindow = false;
    pdfViewerToLeft = false;
    isVendorAccountDisabled = true;
    pdfSource: string = null;
    historyPopUpTitle: string;
    historyMode: string;
    currencyFormat: string;
    previousVendorNotes: string;
    previousWorkflowNotes: string;
    communityColumns: any[];
    vendorPayeeColumns: any[];
    ownerPayeeColumns: any[];
    communityPayeeColumns: any[];
    vendorAccountColumns: any[];
    lateFee: number;
    copiedText: string;
    prorateAmount: number;
    invoiceAttachments: File[] = [];
    settingsMenu: object[];
    pdfWindow: any;
    lateFeeCheck: boolean;
    minimumInvoiceDate: Date;

    continuousProcessMode = false;
    hideAccountEditPopup = false;
    isCopyClicked = false;

    isVendorAccountRequired = false;
    autoPopulatedInvoiceNumber: string = null;

    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconPDF: string = IconConstants.PDF_FILE;
    iconHistory: string = IconConstants.HISTORY;
    iconClose: string = IconConstants.CLOSE;
    iconCheck: string = IconConstants.CHECK;
    iconDelete: string = IconConstants.DELETE;

    isITUser = false;
    isEFTDisabled = false;

    vendorAccountDropDownButtonTemplate: Array<string> = [
        'dropDownEditButton',
        'dropDownRefreshButton',
        'dropDownClearButton'
    ];

    detailsPopupSubscription: Subscription;

    constructor(
        private readonly workflowEditorService: WorkflowEditorService,
        private readonly userService: UserService,
        private readonly fileReader: FileReaderService,
        private readonly router: Router,
        private readonly helperService: HelperService,
        private readonly localStorageService: CCLocalStorageService,
        private readonly errorService: ServerErrorService,
        private readonly communityService: CommunityService,
        private readonly workflowEditorSharedService: WorkflowEditorSharedService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly detailPopupsService: DetailPopupsService
    ) {
        this.customizeLineItemSummary = this.customizeLineItemSummary.bind(this);
    }

    ngOnInit() {
        this.isITUser = this.userService.isITUser();
        this.currencyFormat = StringFormatConstants.currencyEditorFormat;
        this.getSplitterContentPosition();

        if (!this.workflowId) {
            this.continuousProcessMode = true;
            this.loadWorkflowEditor();
        }

        this.detailsPopupSubscription = this.workflowSharedService.getDetailsParams().subscribe((params: any) => {
            const paymentId = Utils.notNullAndDefined(params.paymentId) ? parseInt(params.paymentId) : null;
            const communityId = Utils.notNullAndDefined(params.communityId) ? parseInt(params.communityId) : null;
            const managementCompanyId = Utils.notNullAndDefined(params.managementCompanyId)
                ? parseInt(params.managementCompanyId)
                : null;

            if (
                Utils.notNullAndDefined(paymentId) &&
                Utils.notNullAndDefined(communityId) &&
                Utils.notNullAndDefined(managementCompanyId)
            ) {
                this.detailPopupsService.updateSubjectAPPaymentDetail(managementCompanyId, communityId, paymentId);
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.workflowId && changes.workflowId.currentValue) {
            this.workflowId = changes.workflowId.currentValue;
            this.loadWorkflowEditor();
        }
    }

    ngOnDestroy(): void {
        if (!this.isPopupMode) {
            this.userService.updateSideNavigatorState(true);
            this.userService.updateTopMenuState(true);
        }

        if (Utils.notNullAndDefined(this.detailsPopupSubscription)) {
            this.detailsPopupSubscription.unsubscribe();
        }
    }

    onPdfClick(): void {
        if (this.pdfWindow && !this.pdfWindow.closed) {
            this.pdfWindow.close();
        }
        this.pdfWindow = window.open(
            this.pdfSource,
            '_blank',
            'location=yes,height=670,width=620,scrollbars=yes,status=yes'
        );
    }

    onVendorInstructionsClick(): void {
        if (this.isVendorSelected && this.isVendorInstructionsAvailable) {
            this.showWorkFlowVendorInstructionsPopUp = true;
        }
    }

    onVendorInstructionPopUpClose(): void {
        this.showWorkFlowVendorInstructionsPopUp = false;
    }

    onWorkflowNotesClick(): void {
        this.showWorkFlowNotesPopUp = true;
    }

    onWorkflowNotesPopClose(event): void {
        this.showWorkFlowNotesPopUp = false;
        if (event || this.workFlowEditor.hasNotes) {
            this.workFlowEditor.hasNotes = true;
        }
    }

    onWorkflowHistoryClick(): void {
        this.showWorkFlowHistoryPopUp = true;
    }

    onWorkflowHistoryPopUpClose(): void {
        this.showWorkFlowHistoryPopUp = false;
    }

    onHistoryPopUpClose(): void {
        this.showHistoryPopUp = false;
    }

    onVendorNotesClick(): void {
        if (this.isVendorSelected && !this.isBranchVendor) {
            this.checkVendorNotes();
        }
    }

    onVendorNotesPopClose(): void {
        this.showVendorNotesPopUp = false;
    }

    onCommunitySelection(selectedObject): void {
        this.resetComponents();
        if (selectedObject) {
            const selectedRows = selectedObject.selectedRow;
            if (selectedRows.length > 0) {
                this.selectedCommunity = selectedRows[0];
                this.getDefaults();

                setTimeout(() => {
                    this.isCommunitySelected = true;
                }, 200);
            } else {
                this.selectedCommunity = null;
                this.isCommunitySelected = false;
            }
        } else {
            this.selectedCommunity = null;
            this.isCommunitySelected = false;
        }
    }

    onVendorPayeeSelection(selectedObject, clearCache = false): void {
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const vendorRows = selectedObject.selectedRow;
            if (this.selectedVendorPayee !== vendorRows[0]) {
                this.clearPayeeFilters();
                this.selectedVendorPayee = vendorRows[0];
                this.selectedVendorPayeeIndex = selectedObject.selectedRowIndex;

                this.setWorkflowEFT(this.selectedVendorPayee.isEFTVendor);
                this.modifyWorfkflowEFTAccess(this.selectedVendorPayee.isEFTVendor);
                const numberOfLineBreaks = (
                    this.selectedVendorPayee.displayRemitAddress.match(/\n/g) ||
                    this.selectedVendorPayee.displayRemitAddress.match('<br>') ||
                    []
                ).length;
                this.vendorRemitAddress = this.selectedVendorPayee.displayRemitAddress;
                if (numberOfLineBreaks === 0) {
                    this.vendorRemitAddress += '<br/><br/>';
                }

                this.selectedPayeeName = this.selectedVendorPayee.vendorName;
                this.selectedPayeeCode = this.selectedVendorPayee.vendorCode;
                this.isBranchVendor = this.selectedVendorPayee.isBranchVendor;

                this.isVendorInstructionsAvailable =
                    this.selectedVendorPayee.vendorInstructions !== '' &&
                    this.selectedVendorPayee.vendorInstructions !== null;

                this.resetVendorDependencies();
                this.resetLineItemFields();

                // Marking Vendor Account as required if below condition satisfies and vendor account is enabled( if workflow.allowProcessing is true and vendor is Branch Vendor)
                this.isVendorAccountRequired = Utils.isInList(this.selectedVendorPayee.vendorTypeCode, ['A', 'I', 'U']);

                if (this.isBranchVendor) {
                    const customDialog = custom({
                        title: 'Setup Community Vendor',
                        messageHtml: `<b>${this.selectedVendorPayee.vendorName}</b> is not added to <b>${this.selectedCommunity.communityName}</b>.</br>Do you want to add this vendor to the Community?`,
                        buttons: [
                            {
                                text: 'OK',
                                onClick: (e) => {
                                    this.createCommunityVendor();
                                }
                            },
                            {
                                text: 'CANCEL',
                                onClick: (e) => {
                                    return;
                                }
                            }
                        ]
                    });
                    customDialog.show();
                } else if (!this.isBranchVendor) {
                    this.processCommunityVendorSelection(clearCache);
                }
            }
        } else {
            this.selectedVendorPayee = null;
            this.clearPayeeFilters();
            this.resetLineItemFields();
            this.setVendorAccountEnableDisable();
        }
    }

    onOwnerPayeeSelection(selectedObject): void {
        this.clearPayeeFilters();
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const ownerRows = selectedObject.selectedRow;

            this.selectedOwnerPayee = ownerRows[0];
            this.selectedPayeeName = this.selectedOwnerPayee.ownerName;
            this.selectedPayeeCode = this.selectedOwnerPayee.customerId;
        } else {
            this.selectedOwnerPayee = null;
        }
    }

    onCommunityPayeeSelection(selectedObject): void {
        this.clearPayeeFilters();

        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const communityRows = selectedObject.selectedRow;

            this.selectedCommunityPayee = communityRows[0];
            this.selectedPayeeName = this.selectedCommunityPayee.communityName;
            this.selectedPayeeCode = this.selectedCommunityPayee.intacctCompanyId;
        } else {
            this.selectedCommunityPayee = null;
        }
    }

    onPayeeTypeChanged(event): void {
        this.clearPayeeFilters();

        this.selectedPayee = event.value;

        if (this.selectedCommunity) {
            if (this.selectedPayee === 'OWNER') {
                this.getOwnerData();
            }
            if (this.selectedPayee === 'COMMUNITY') {
                this.payeeCommunityBoxGridSource = this.communityGridSource;
            }
            if (this.selectedPayee === 'VENDOR') {
                this.getVendorData();
            }
            this.populatedInvoiceNumber();
        }
    }

    onAccountEditClick(): void {
        this.showManageVendorAccountPopup = true;
    }

    onRefreshAccountEditSource(): void {
        this.getVendorAccount(true);
    }

    onManageVendorAccountClose(): void {
        this.showManageVendorAccountPopup = false;
        this.getVendorAccount(true);
    }

    onVendorAccountEditorClose(event): void {
        this.showAddEditVendorAccount = false;
        this.showManageVendorAccountPopup = true;

        if (event) {
            if (!!this.selectedVendorAccountForEditor) {
                setTimeout(() => this.manageVendorAccount.refreshVendorAccountNotes(), 250);
                setTimeout(() => this.manageVendorAccount.refreshVendorAccountHistory(), 250);
            }

            setTimeout(() => this.manageVendorAccount.reloadManageAccounts(), 250);
        }
    }

    onVendorAccountSelectionForEditor(event): void {
        this.selectedVendorAccountForEditor = event;
        if (!!this.selectedVendorAccountForEditor) {
            this.addEditVendorAccountPopUpTitle = 'Edit Vendor Account';
            this.vendorAccountConfigMode = 'edit';
        } else {
            this.addEditVendorAccountPopUpTitle = 'Add Vendor Account';
            this.vendorAccountConfigMode = 'add';
        }
        this.showAddEditVendorAccount = true;
        this.showManageVendorAccountPopup = false;
    }

    onAccountRefreshClick(event): void {
        const pointerEvent = event.event;
        pointerEvent.stopPropagation();
        this.getVendorAccount(true);
    }

    onWorkflowClose(): void {
        this.unlockWorkflow();

        this.closeOrExitWorkflowEditor(false);
    }

    onVendorHistoryClick(): void {
        this.setVendorHistoryParams();
    }

    onAccountHistoryClick(): void {
        this.historyPopUpTitle = 'Vendor Account History';
        this.historyMode = 'vendorAccountHistory';

        this.vendorAccountHistoryBaseParams = new WFVendorHistoryParametersDto();
        this.vendorAccountHistoryBaseParams.communityId = this.selectedCommunity.communityId;
        this.vendorAccountHistoryBaseParams.vendorCode = this.selectedVendorPayee.vendorCode;
        this.vendorAccountHistoryBaseParams.vendorAccountId = this.selectedVendorAccountId;

        this.showHistoryPopUp = true;
    }

    onInvoiceHistoryClick(): void {
        this.historyPopUpTitle = 'Invoice History';
        this.historyMode = 'invoiceHistory';

        this.invoiceHistoryBaseParams = new WFInvoiceHistoryParametersDto();
        this.invoiceHistoryBaseParams.communityId = this.selectedCommunity.communityId;
        this.invoiceHistoryBaseParams.payeeCode = this.selectedPayeeCode;
        this.invoiceHistoryBaseParams.invoiceNumber = this.workFlowEditor.invoiceNumber;

        this.showHistoryPopUp = true;
    }

    onVendorAccountSelection(selectedObject): void {
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            this.selectedVendorAccount = selectedObject.selectedRow[0];
            this.selectedVendorAccountId = this.selectedVendorAccount.id;
            this.setWorkflowEFT(this.selectedVendorAccount.eftActive);
            this.getVendorAccountTemplate();
            this.vendorAccountDropDownButtonTemplate = [
                'dropDownEditButton',
                'dropDownRefreshButton',
                'dropDownClearButton'
            ];
        } else {
            this.selectedVendorAccount = null;
            this.selectedVendorAccountId = null;
            this.copiedText = null;
            if (!!this.selectedVendorPayee) {
                this.setWorkflowEFT(this.selectedVendorPayee.isEFTVendor);
                this.modifyWorfkflowEFTAccess(this.selectedVendorPayee.isEFTVendor);
                this.getVendorTemplate();
            }
            this.vendorAccountDropDownButtonTemplate = ['dropDownEditButton', 'dropDownRefreshButton'];
        }
        this.populatedInvoiceNumber();
    }

    onVendorAccountCopyClick(): void {
        if (!!this.selectedVendorAccount) {
            this.copiedText = this.selectedVendorAccount.accountNumber;
            this.isCopyClicked = true;

            setTimeout(() => {
                this.isCopyClicked = false;
            }, 2000);
        }
    }

    onInvoiceNumberPasteClick(): void {
        if (!!this.copiedText) {
            if (!!this.workFlowEditor.invoiceNumber) {
                this.workFlowEditor.invoiceNumber += this.copiedText;
            } else {
                this.workFlowEditor.invoiceNumber = this.copiedText;
            }
        }
    }

    onDescriptionPasteClick(): void {
        if (!!this.copiedText) {
            if (this.workFlowEditor.description) {
                this.workFlowEditor.description += this.copiedText;
            } else {
                this.workFlowEditor.description = this.copiedText;
            }
        }
    }

    onSpecialHandlingValueChange(): void {
        this.setSpecialHandling();
    }

    onInvoiceDateChanged(): void {
        if (!!this.workFlowEditor.invoiceDate) {
            const dueDate = moment(this.workFlowEditor.invoiceDate).add(30, 'days').toDate();
            this.workFlowEditor.dueDate = dueDate;
        }
        this.populatedInvoiceNumber();
    }

    onReplaceInvoicePopUpClose(): void {
        this.showReplaceInvoiceFilePopUp = false;
        this.invoiceAttachments = [];
    }

    onReplaceInvoiceSubmit(): void {
        this.replaceInvoiceSubmit();
        this.invoiceAttachments = [];
    }

    onDeleteWorkflow(reason: string): void {
        this.closeAllPopup();
        this.deleteWorkflow(reason);
    }

    onDeleteWorkflowClose(): void {
        this.showDeleteWorkflowPopUp = false;
    }

    onVerifyAndApprove(): void {
        if (this.fillWorkflowEditorRequiredFields()) {
            this.closeAllPopup();
            this.verifyAndApproveWorkflow();
        }
    }

    onAddNewRowToWorkflowGrid(): void {
        this.insertDataGridNewRow();
    }

    onDefaultLineItems(): void {
        if (Utils.notNullOrEmpty(this.wfItemsDataGridSource)) {
            const dialog = this.helperService.getConfirmationDialog(
                `Confirmation`,
                `There are line item changes. Would you like to continue by setting default line items?`,
                'Yes',
                'No'
            );
            dialog.show().then((answer: boolean) => {
                if (Utils.isTrue(answer)) {
                    this.resetLineItems();
                    this.setDefaultLineItems();
                }
            });
        } else {
            this.resetLineItems();
            this.setDefaultLineItems();
        }
    }

    onDeleteFromWorkflowGrid(record): void {
        const wfItemsDataModifiedGridSource: WFEWorkflowItemRawDto[] = [];
        this.wfItemsDataGridSource.forEach((ele) => {
            if (ele.sequence === record.sequence) {
                ele.crudAction = 'delete';
            }
            wfItemsDataModifiedGridSource.push(ele);
        });

        this.wfItemsDataGridSource = [];
        let itemCount = 0;
        wfItemsDataModifiedGridSource.forEach((ele) => {
            if (ele.crudAction !== 'delete') {
                ele.sequence = itemCount + 1;
                itemCount += 1;
                this.wfItemsDataGridSource.push(ele);
            }
        });

        this.workflowDataGrid.instance.repaint();
        this.workFlowEditor.items = wfItemsDataModifiedGridSource;
    }

    onRowUpdated(event): void {
        const newRow = event.data;
        this.wfItemsDataGridSource.forEach((item) => {
            if (item.sequence === newRow.sequence) {
                item.description = newRow.description;
                item.itemAmount = newRow.itemAmount;

                if (item.crudAction) {
                    if (item.crudAction !== 'add') {
                        item.crudAction = 'edit';
                    }
                } else {
                    item.crudAction = 'edit';
                }
            }
        });
    }

    onProrateTaxChanges(): void {
        this.showProrateTaxChanges = true;
    }

    onProrateTaxChangesClose(): void {
        this.showProrateTaxChanges = false;
    }

    onProrateTaxSubmit(): void {
        const totalAmount = this.workflowDataGrid.instance.getTotalSummaryValue('itemAmount');
        this.wfItemsDataGridSource.forEach((item) => {
            const prorateAmount = item.itemAmount + (item.itemAmount / totalAmount) * this.prorateAmount;
            item.itemAmount = prorateAmount;
            item.crudAction = 'edit';
        });

        this.prorateAmount = null;
        this.showProrateTaxChanges = false;
    }

    onVendorEditClick(): void {
        const checkStatus = this.checkRequiredWorkflowFields();

        if (checkStatus.isValid) {
            const customDialog = custom({
                title: 'Confirmation',
                messageHtml:
                    '<div class="confirmation-message-container"><i class="fas fa-question-circle pop-up-icon"></i>Move this workflow to the Vendor Editor inbox?</div>',
                buttons: [
                    {
                        text: 'Yes',
                        type: 'success',
                        onClick: (e) => {
                            this.sendWorkFlowToVendorEdit();
                        }
                    },
                    {
                        text: 'No',
                        type: 'default',
                        onClick: (e) => {
                            return;
                        }
                    }
                ]
            });
            customDialog.show();
        } else {
            this.workflowMessageBox.showErrorMessage('Error', checkStatus.message);
        }
    }

    customizeLineItemSummary(e): string {
        return 'Total: ' + formatNumber(e.value, this.currencyFormat);
    }

    onWorkflowOptionSelection(event): void {
        const itemData = event.itemData;

        if (itemData.name === 'deleteWorkflow') {
            this.showDeleteWorkflowPopUp = true;
        } else if (itemData.name === 'replaceWorkflow') {
            this.showReplaceInvoiceFilePopUp = true;
        } else if (itemData.name === 'sendWorkflow') {
            if (this.fillWorkflowEditorRequiredFields()) {
                this.sendWorkflowToSupervisor();
            }
        } else if (itemData.name === 'transferToCorp') {
            this.transferWorkflowToCorpQueue();
        }
    }

    onAmountToPayChanged(event): void {
        this.updateLineItem();
    }

    onDescriptionChanged(event): void {
        this.updateLineItem();
    }

    getWorkFlowCloseLabel(): string {
        return this.isPopupMode ? 'Close' : 'Exit';
    }

    onScrollViewInitialize(): void {
        setTimeout(() => {
            if (this.workflowScrollView && this.workflowScrollView.instance) {
                this.workflowScrollView.instance.scrollBy(1000);
            }
        }, 1000);
    }

    onAddVendorAccountClick(): void {
        this.vendorAccountConfigMode = ActionConstants.ADD;
        this.addEditVendorAccountPopUpTitle = 'Add Vendor Account';
        this.showAddEditVendorAccount = true;
    }

    onEFTValueChanged(event: any): void {
        if (
            Utils.notNullAndDefined(event) &&
            Utils.notNullAndDefined(event.value) &&
            Utils.notNullAndDefined(this.selectedVendorPayee) &&
            Utils.isFalse(this.selectedVendorPayee.isEFTVendor) &&
            Utils.notNullAndDefined(this.selectedVendorAccount) &&
            Utils.notEquals(this.selectedVendorAccount.eftActive, event.value)
        ) {
            // show Confirmation Do you wish to set EFT Flag for the Vendor Account to Inactive?
            const eftState = Utils.isTrue(event.value) ? `Active` : 'Inactive';
            const confirmationMessage = `Do you wish to set EFT Flag for the Vendor Account to ${eftState}?`;
            const dialog = this.helperService.getConfirmationDialog('Confirmation', confirmationMessage);
            dialog.show().then((answer) => {
                if (Utils.isTrue(answer)) {
                    const baseParams: WFEVendorAccountEFTActiveStatusParameterDto =
                        new WFEVendorAccountEFTActiveStatusParameterDto();
                    baseParams.communityId = this.selectedCommunity.communityId;
                    baseParams.eftActive = event.value;
                    baseParams.payeeCode = this.selectedPayeeCode;
                    baseParams.vendorAccountId = this.selectedVendorAccountId;
                    baseParams.workflowId = this.workflowId;

                    this.workflowEditorSharedService.updateVendorAccountEFT(baseParams).subscribe(
                        () => {
                            notify(
                                'Vendor Account EFT successfully updated',
                                'Success',
                                MessageInterval.successMsgInterval
                            );
                            this.getVendorAccount(true);
                        },
                        (error) => {
                            this.errorService.displayError(error);
                        }
                    );
                }
            });
        }
    }

    private populatedInvoiceNumber(): void {
        const newAutoPopulatedInvoiceNumber = this.generateInvoiceNumber();
        if (
            Utils.notEqualsIgnoreCase(newAutoPopulatedInvoiceNumber, this.autoPopulatedInvoiceNumber) &&
            (Utils.equalsIgnoreCase(this.workFlowEditor.invoiceNumber, this.autoPopulatedInvoiceNumber) ||
                Utils.isNullOrEmpty(this.workFlowEditor.invoiceNumber))
        ) {
            this.workFlowEditor.invoiceNumber = newAutoPopulatedInvoiceNumber;
        }
        this.autoPopulatedInvoiceNumber = newAutoPopulatedInvoiceNumber;
    }

    private generateInvoiceNumber(): string {
        let generatedInvoiceNumber = null;
        if (
            Utils.notNullOrEmpty(this.workFlowEditor.invoiceDate) &&
            Utils.equalsIgnoreCase(this.selectedPayee, 'VENDOR') &&
            Utils.notNullAndDefined(this.selectedVendorPayee) &&
            Utils.isInList(this.selectedVendorPayee.vendorTypeCode, ['A', 'U']) &&
            Utils.notNullAndDefined(this.selectedVendorAccount)
        ) {
            // replacing all occurence of '-' and ' ' with '' from selectedVendorAccount.accountNumber.
            const formattedVendorAccountNumber = this.selectedVendorAccount.accountNumber
                .replace(/-/g, '')
                .replace(/ /g, '')
                .trim();
            const formattedInvoiceDate = moment(this.workFlowEditor.invoiceDate).format('MMYY');
            generatedInvoiceNumber = `${formattedVendorAccountNumber}-${formattedInvoiceDate}`;
        }
        return generatedInvoiceNumber;
    }

    private updateLineItem(): void {
        if (this.wfItemsDataGridSource.length === 1) {
            this.wfItemsDataGridSource.forEach((item) => {
                item.itemAmount = this.workFlowEditor.amount;
                item.description = this.workFlowEditor.description;
            });

            this.workflowDataGrid.instance.refresh();
        }
    }

    private fillWorkflowEditorRequiredFields(): boolean {
        const checkStatus = this.checkRequiredWorkflowFields();
        if (checkStatus.isValid) {
            this.workFlowEditor.communityId = this.selectedCommunity.communityId;
            this.workFlowEditor.vendorAccountId = this.selectedVendorAccountId;
            this.workFlowEditor.items = this.wfItemsDataGridSource;
            this.workFlowEditor.payeeCode = this.selectedPayeeCode;
            this.workFlowEditor.payeeType = this.selectedPayee;
        } else {
            this.workflowMessageBox.showErrorMessage('Error', checkStatus.message);
        }
        return checkStatus.isValid;
    }

    private checkRequiredWorkflowFields(): any {
        const resultStatus = { isValid: true, message: '' };

        if (this.selectedPayee === 'VENDOR') {
            if (!this.selectedVendorPayee) {
                resultStatus.isValid = false;
                resultStatus.message = 'Vendor Payee is required.';
            }
        } else if (this.selectedPayee === 'OWNER') {
            if (!this.selectedOwnerPayee) {
                resultStatus.isValid = false;
                resultStatus.message = 'Owner Payee is required.';
            }
        } else if (this.selectedPayee === 'COMMUNITY') {
            if (!this.selectedCommunityPayee) {
                resultStatus.isValid = false;
                resultStatus.message = 'Community Payee is required.';
            }
        }

        if (!this.workFlowEditor.invoiceDate) {
            resultStatus.isValid = false;
            resultStatus.message = 'Invoice Date is required.';
            this.invoiceDate.instance.focus();
            this.invoiceDate.validationStatus = 'invalid';
        } else if (!this.workFlowEditor.dueDate) {
            resultStatus.isValid = false;
            resultStatus.message = 'Due Date is required.';
            this.dueDate.instance.focus();
            this.dueDate.validationStatus = 'invalid';
        } else if (this.workFlowEditor.dueDate < this.workFlowEditor.invoiceDate) {
            resultStatus.isValid = false;
            resultStatus.message = 'Due Date should be greater than Invoice Date.';
            this.dueDate.instance.focus();
            this.dueDate.validationStatus = 'invalid';
        } else if (!this.workFlowEditor.invoiceNumber) {
            resultStatus.isValid = false;
            resultStatus.message = 'Invoice Number is required.';
            this.invoiceNumber.instance.focus();
            this.invoiceNumber.validationStatus = 'invalid';
        } else if (this.workFlowEditor.amount > this.workFlowEditor.invoiceAmount) {
            resultStatus.isValid = false;
            resultStatus.message = 'Amount to be paid must less than or equal to total invoice';
        } else if (this.workflowDataGrid && this.workflowDataGrid.instance) {
            if (this.wfItemsDataGridSource.length === 0) {
                resultStatus.isValid = false;
                resultStatus.message = 'At least one line item is required.';
            } else {
                this.wfItemsDataGridSource.forEach((lineItem) => {
                    if ((!lineItem.itemAmount || lineItem.itemAmount === 0) && lineItem.crudAction !== 'delete') {
                        resultStatus.isValid = false;
                        resultStatus.message = 'Line item amount is required and cannot be zero.';
                    }
                });
            }
        } else if (!this.workFlowEditor.specialHandlingId) {
            if (this.workFlowEditor.specialHandlingId >= 0) {
                resultStatus.isValid = true;
            }

            if (!resultStatus.isValid) {
                resultStatus.message = 'Handling is required.';
                this.specialHandling.instance.focus();
                this.specialHandling.validationStatus = 'invalid';
            }
        }

        if (this.lateFeeCheck) {
            if (!this.workFlowEditor.lateFee) {
                resultStatus.isValid = false;
                resultStatus.message = 'Late fee is required.';
            }
        }

        return resultStatus;
    }

    private setVendorAccountEnableDisable(): void {
        this.isVendorAccountDisabled =
            !this.isVendorSelected || this.isBranchVendor || !this.workFlowEditor.allowProcessing;
    }

    private clearPayeeFilters(): void {
        this.selectedVendorPayee = null;
        this.selectedOwnerPayee = null;
        this.selectedCommunityPayee = null;
        this.selectedPayeeCode = null;
        this.vendorRemitAddress = '<br><br>';
        this.isVendorInstructionsAvailable = false;
        this.selectedVendorAccount = null;
        this.selectedVendorAccountId = null;
        this.copiedText = null;
        this.vendorAccountSource = [];
        this.vendorAccountBoxValue = [];
        this.isVendorAccountRequired = false;
        if (this.vendorAccountSelector) {
            this.vendorAccountSelector.resetSelector();
        }

        setTimeout(() => {
            this.isVendorSelected = false;
            this.setVendorAccountEnableDisable();
        }, 500);
    }

    private resetLineItems(): void {
        this.wfItemsDataGridSource = [];
        if (!!this.workflowDataGrid && !!this.workflowDataGrid.instance) {
            this.workflowDataGrid.instance.refresh();
        }
    }

    private resetLineItemFields(): void {
        this.wfItemsDataGridSource.forEach((ele) => {
            ele.templateId = null;
        });
    }

    private resetVendorDependencies(): void {
        this.selectedVendorAccount = null;
        this.selectedVendorAccountId = null;
        this.copiedText = null;
        this.vendorAccountSource = [];
        this.vendorAccountBoxValue = [];
        if (this.vendorAccountSelector) {
            this.vendorAccountSelector.resetSelector();
        }

        // Pop up refresh
        if (this.isBranchVendor) {
            this.showHistoryPopUp = false;
            this.showVendorNotesPopUp = false;
            this.showWorkFlowVendorInstructionsPopUp = false;
        } else {
            if (this.showHistoryPopUp) {
                this.setVendorHistoryParams();
                this.showHistoryPopUp = false;
            }

            if (this.showVendorNotesPopUp) {
                this.checkVendorNotes();
            }

            if (this.showWorkFlowVendorInstructionsPopUp) {
                this.showWorkFlowVendorInstructionsPopUp = false;
                if (this.isVendorInstructionsAvailable) {
                    this.showWorkFlowVendorInstructionsPopUp = true;
                }
            }
        }
    }

    private resetComponents(): void {
        this.selectedPayee = this.payeeOptions[0].value;
        this.vendorBillToAddress = '<br><br>';
        this.showHistoryPopUp = false;
        this.showVendorNotesPopUp = false;
        this.showWorkFlowHistoryPopUp = false;
        this.showWorkFlowNotesPopUp = false;
        this.showWorkFlowVendorInstructionsPopUp = false;

        if (this.selectedPayee === 'VENDOR' && this.vendorPayeeSelector) {
            this.vendorPayeeSelector.resetSelector();
        } else if (this.selectedPayee === 'OWNER' && this.ownerPayeeSelector) {
            this.ownerPayeeSelector.resetSelector();
        } else if (this.selectedPayee === 'COMMUNITY' && this.communityPayeeSelector) {
            this.communityPayeeSelector.resetSelector();
        }

        this.clearPayeeFilters();
        this.resetLineItems();
    }

    private resetWorkflow(): void {
        this.resetComponents();

        this.selectedCommunity = null;
        this.communityGridBoxValue = null;
        this.communitySelector.resetSelector();

        if (this.pdfWindow) {
            this.pdfWindow.close();
        }
    }

    private getDefaults(): void {
        this.getVendorData();
        this.setEditorRawLineItems();
        this.getWorkflowOptions();
        this.getCommunityBillToAddress();
        this.getSpecialHandling();
    }

    private setDefaultComponentValues(): void {
        this.pdfSource = null;
        this.pdfURL.emit(null);
        this.payeeOptions = [
            {
                value: 'VENDOR',
                displayName: 'Vendor'
            },
            {
                value: 'OWNER',
                displayName: 'Owner'
            },
            {
                value: 'COMMUNITY',
                displayName: 'Community'
            }
        ];
        this.communityColumns = this.communityService.getCommunitySelectorColumns();
        this.vendorPayeeColumns = this.getVendorPayeeColumns();
        this.ownerPayeeColumns = this.getOwnerPayeeColumns();
        this.communityPayeeColumns = this.getCommunityPayeeColumns();
        this.vendorAccountColumns = this.getVendorAccountColumns();
    }

    private setVendorHistoryParams(): void {
        this.historyPopUpTitle = 'Vendor History';
        this.historyMode = 'vendorHistory';

        this.vendorHistoryBaseParams = new WFVendorHistoryParametersDto();
        this.vendorHistoryBaseParams.communityId = this.selectedCommunity.communityId;
        this.vendorHistoryBaseParams.vendorCode = this.selectedVendorPayee.vendorCode;

        this.showHistoryPopUp = true;
    }

    private getOwnerPayeeColumns(): any[] {
        return [
            {
                dataField: 'ownerName',
                dataType: 'string',
                caption: 'Name'
            },
            {
                dataField: 'propertyAddress',
                dataType: 'string',
                caption: 'Property Address'
            },
            {
                dataField: 'customerId',
                dataType: 'string',
                caption: 'Customer Id'
            },
            {
                dataField: 'isCurrentOwner',
                dataType: 'boolean',
                caption: 'Current Owner',
                cellTemplate: 'trueValueCellTemplate'
            }
        ];
    }

    private getVendorPayeeColumns(): any[] {
        return [
            {
                dataField: 'vendorName',
                dataType: 'string',
                caption: 'Name'
            },
            {
                dataField: 'remitAddress',
                dataType: 'string',
                caption: 'Address'
            },
            {
                dataField: 'isActive',
                dataType: 'boolean',
                caption: 'Active',
                cellTemplate: 'trueValueCellTemplate'
            }
        ];
    }

    private getCommunityPayeeColumns(): any[] {
        return [
            {
                dataField: 'locationName',
                dataType: 'string',
                caption: 'Location'
            },
            {
                dataField: 'communityName',
                dataType: 'string',
                caption: 'Community'
            },
            {
                dataField: 'intacctCompanyId',
                dataType: 'string',
                caption: 'Intacct Company Id'
            },
            {
                dataField: 'status',
                dataType: 'string',
                caption: 'Status'
            }
        ];
    }

    private getVendorAccountColumns(): any[] {
        return [
            {
                dataField: 'accountNumber',
                dataType: 'string',
                caption: 'Account Number',
                width: 110
            },
            {
                dataField: 'description',
                dataType: 'string',
                caption: 'Description'
            },
            {
                dataField: 'active',
                dataType: 'boolean',
                caption: 'Active',
                cellTemplate: 'trueValueCellTemplate',
                width: 80
            },
            {
                dataField: 'eftActive',
                dataType: 'boolean',
                caption: 'EFT Active',
                cellTemplate: 'trueValueCellTemplate',
                width: 80
            }
        ];
    }

    private checkVendorNotes(): void {
        if (this.selectedVendorPayee.hasNotes) {
            this.showVendorNotesPopUp = true;
        } else {
            this.workflowMessageBox.showInfoMessage(
                'Vendor Notes',
                this.selectedVendorPayee.vendorName + ' has no notes.'
            );
            this.showVendorNotesPopUp = false;
        }
    }

    private setDefaultLineItems(): void {
        this.wfItemsDataGridSource = [];
        this.defaultLineItems.forEach((ele) => {
            const item: WFEWorkflowItemRawDto = new WFEWorkflowItemRawDto();
            const sequence = this.defaultLineItems.indexOf(ele) + 1;
            item.description = ele.description;
            item.itemAmount = ele.itemAmount;
            item.dimensions = ele.dimensions;
            item.templateId = ele.templateId;
            item.sequence = sequence;
            item.crudAction = 'add';

            this.wfItemsDataGridSource.push(item);
        });
        this.workflowDataGrid.instance.refresh();
    }

    private setPayeeRawValues(): void {
        if (!!this.workFlowEditor.payeeType) {
            if (this.workFlowEditor.payeeType.toUpperCase() === 'VENDOR') {
                this.payeeVendorGridBoxValue = this.workFlowEditor.payeeCode ? [this.workFlowEditor.payeeCode] : null;
            } else if (this.workFlowEditor.payeeType.toUpperCase() === 'OWNER') {
                this.payeeOwnerGridBoxValue = this.workFlowEditor.payeeCode ? [this.workFlowEditor.payeeCode] : null;
            } else if (this.workFlowEditor.payeeType.toUpperCase() === 'COMMUNITY') {
                this.payeeCommunityGridBoxValue = this.workFlowEditor.payeeCode
                    ? [this.workFlowEditor.payeeCode]
                    : null;
            }
        }
    }

    private setEditorRawLineItems(): void {
        const lineItemsGridSource = this.workFlowEditor.items;
        this.wfItemsDataGridSource = [];
        lineItemsGridSource.forEach((ele) => {
            let obj: WFEWorkflowItemRawDto = new WFEWorkflowItemRawDto();
            obj = Object.assign(obj, ele);
            obj.sequence = lineItemsGridSource.indexOf(ele) + 1;
            obj.crudAction = 'add';

            this.wfItemsDataGridSource.push(obj);
        });
    }

    private setVendorAccount(): void {
        if (this.selectedVendorAccountId) {
            this.vendorAccountBoxValue = this.selectedVendorAccountId ? [this.selectedVendorAccountId] : null;
            this.vendorAccountSource.forEach((ele) => {
                if (ele.id === this.selectedVendorAccountId) {
                    this.selectedVendorAccount = ele;
                }
            });
            this.getVendorAccountTemplate();
        } else {
            this.selectedVendorAccount = null;
            this.copiedText = null;
        }
    }

    private setSpecialHandling(): void {
        if (this.workFlowEditor.specialHandlingId) {
            this.specialHandlingSource.forEach((ele) => {
                if (ele.specialHandlingId === this.workFlowEditor.specialHandlingId) {
                    this.selectedSpecialHandling = ele;
                }
            });
        }
    }

    private setDefaultSpecialHandling(): void {
        if (!this.workFlowEditor.specialHandlingId) {
            this.workFlowEditor.specialHandlingId = 0;
        }
    }

    private checkIfHandlingExists(handling: number): boolean {
        let isValid = false;
        if (this.specialHandlingSource.length > 0) {
            this.specialHandlingSource.forEach((ele) => {
                if (ele.specialHandlingId === handling) {
                    isValid = true;
                }
            });
        }

        return isValid;
    }

    private setDefaultRawValues(): void {
        // [ANG-2510] - Data Entry Steps - Do not prepopulate community
        this.communityGridBoxValue = null; // this.workFlowEditor.communityId ? [this.workFlowEditor.communityId] : null;
        this.selectedPayee = this.workFlowEditor.payeeType ? this.workFlowEditor.payeeType : this.payeeOptions[0].value;
    }

    private setWorkflowEFT(isEFT: boolean): void {
        this.workFlowEditor.isEFTAccount = isEFT;
    }

    private modifyWorfkflowEFTAccess(isEFT: boolean): void {
        this.isEFTDisabled = isEFT && !this.isITUser;
    }

    private getWorkflowOptions(): void {
        this.workflowOptions = [
            {
                name: 'replaceWorkflow',
                text: 'Replace Invoice',
                icon: IconConstants.EXCHANGE,
                visible: this.workFlowEditor.allowReplaceInvoice,
                type: 'default'
            },
            {
                name: 'transferToCorp',
                text: 'Transfer to Corp Work Queue',
                icon: IconConstants.RANDOM,
                visible: this.workFlowEditor.allowWorkQueueTransfer,
                type: 'default'
            },
            {
                name: 'sendWorkflow',
                text: 'Send To Supervisor',
                icon: IconConstants.EDIT,
                visible: this.workFlowEditor.allowSendToSupervisor,
                type: 'default'
            },
            {
                name: 'deleteWorkflow',
                text: 'Delete AAP Workflow',
                icon: IconConstants.DELETE,
                visible: this.workFlowEditor.allowDelete,
                type: 'danger'
            }
        ];
    }

    private transferWorkflowToCorpQueue(): void {
        const customDialog = custom({
            title: 'Confirmation',
            messageHtml: `<div class="confirmation-message-container"><i class="fas fa-question-circle pop-up-icon"></i>Are you sure you want to transfer${this.workflowId}<br/> to Corp Work Queue ?</div>`,
            buttons: [
                {
                    text: 'Yes',
                    type: 'success',
                    onClick: (e) => {
                        this.closeAllPopup();
                        this.transferWorkQueue();
                    }
                },
                {
                    text: 'No',
                    type: 'default',
                    onClick: (e) => {
                        return;
                    }
                }
            ]
        });
        customDialog.show();
    }

    private getDataEntryEditor(): void {
        setTimeout(() => {
            this.loadingVisible = true;
        }, 100);

        const baseParams: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParams.communityId = this.communityId;
        baseParams.workflowId = this.workflowId;
        baseParams.clearCache = true;

        this.workflowEditorService.dataEntryEditor(baseParams).subscribe(
            (result: WFEWorkflowRawDto) => {
                setTimeout(() => {
                    this.loadingVisible = false;
                }, 100);
                this.prepareWorkFlowEditor(result);
            },
            (error: HttpErrorResponse) => {
                this.showDataEntryErrorMessage(error, 'Unknown error occured');
            }
        );
    }

    private getNextDataEntryEditor(): void {
        this.loadingVisible = true;
        this.workFlowEditor = new WFEWorkflowRawDto();
        this.workflowEditorService.nextDataEntryEditor().subscribe(
            (result: WFEWorkflowRawDto) => {
                if (result) {
                    this.prepareWorkFlowEditor(result);
                } else {
                    this.workflowMessageBox.showWithFixedWidthAndIcon(
                        CCMessageBoxIcon.info,
                        'Information',
                        `There are no workflow to process`,
                        320,
                        () => {
                            this.loadingVisible = false;
                            this.closeOrExitWorkflowEditor(true);
                        }
                    );
                }
                this.loadingVisible = false;
            },
            (error: HttpErrorResponse) => {
                this.showDataEntryErrorMessage(error, 'Unknown error occured');
            }
        );
    }

    private getWorkflowInvoice(clearCache = false): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.clearCache = clearCache;

        this.loadingVisible = true;
        this.pdfLoader.emit(true);
        this.workflowEditorService.downloadWorkflowInvoice(baseParams).subscribe(
            (result: Blob) => {
                if (!!result) {
                    const fileURL: string = this.helperService.getFileUrlFromBlob(result);

                    this.pdfSource = fileURL;
                    this.pdfURL.emit(fileURL);

                    this.pdfLoader.emit(false);
                    if (this.separateWindow) {
                        this.onPdfClick();
                    }
                }
                this.loadingVisible = false;
            },
            (error) => {
                if (error.status === 404) {
                    this.showAPIErrorMessage(
                        error,
                        'Something went wrong, We are unable to find the invoice. Please try again later.'
                    );
                } else {
                    this.showAPIErrorMessage(error);
                }
                this.pdfSource = null;
                this.pdfURL.emit(null);
                this.pdfLoader.emit(false);
                this.loadingVisible = false;
            }
        );
    }

    private getCommunityData(): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        this.workflowEditorService.getWorkflowEditorCommunities(baseParams).subscribe(
            (result: WFECommunityResultDto[]) => {
                this.communityGridSource = result;
                this.setDefaultRawValues();
            },
            (error) => {
                this.showAPIErrorMessage(error);
            }
        );
    }

    private getOwnerData(): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;

        this.workflowEditorService.getWorkflowEditorOwners(baseParams).subscribe(
            (result: WFEOwnerResultDto[]) => {
                this.payeeOwnerBoxGridSource = result;
            },
            (error) => {
                this.showAPIErrorMessage(error);
            }
        );
    }

    private getVendorData(clearCache = false): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.clearCache = clearCache;

        this.workflowEditorService.getWorkflowEditorVendors(baseParams).subscribe(
            (result: WFEVendorResultDto[]) => {
                this.payeeVendorBoxGridSource = result;
                this.setPayeeRawValues();

                if (clearCache) {
                    const selectedRow = this.payeeVendorBoxGridSource.find(
                        (d) => d.vendorCode === this.selectedVendorPayee.vendorCode
                    );
                    const selectedRowIndex = this.payeeVendorBoxGridSource.findIndex(
                        (d) => d.vendorCode === this.selectedVendorPayee.vendorCode
                    );
                    const selectedObject = {
                        selectedRow: [selectedRow],
                        selectedRowIndex
                    };

                    this.onVendorPayeeSelection(selectedObject, clearCache);
                }

                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private processCommunityVendorSelection(clearCache = false): void {
        if (!this.isBranchVendor) {
            this.getVendorAccount();
            this.getVendorTemplate(clearCache);

            setTimeout(() => {
                this.isVendorSelected = true;
                this.setVendorAccountEnableDisable();
                this.getWorkflowOptions();
            }, 500);
        }
    }

    private getSpecialHandling(clearCache = false): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.clearCache = clearCache;

        this.workflowEditorSharedService.getWorkflowSpecialHandling(baseParams).subscribe(
            (result: WFESpecialHandlingResultDto[]) => {
                this.specialHandlingSource = result;
                if (this.specialHandlingSource.length > 0) {
                    this.setSpecialHandling();
                }
            },
            (error) => {
                this.showAPIErrorMessage(error);
            }
        );
    }

    private getVendorAccount(clearCache = false): void {
        const baseParams: WFEVendorParametersDto = new WFEVendorParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.vendorCode = this.selectedVendorPayee.vendorCode;
        baseParams.clearCache = clearCache;

        this.workflowEditorService.getWorkflowVendorAccount(baseParams).subscribe(
            (result: WFEVendorAccountResultDto[]) => {
                this.vendorAccountSource = result;
                if (this.vendorAccountSource.length > 0) {
                    this.setVendorAccount();
                    this.vendorAccountDropDownButtonTemplate = [
                        'dropDownEditButton',
                        'dropDownRefreshButton',
                        'dropDownClearButton'
                    ];
                    if (Utils.equals(this.vendorAccountSource.length, 1)) {
                        this.vendorAccountBoxValue = [this.vendorAccountSource[0].id];
                        this.populatedInvoiceNumber();
                    }
                } else {
                    this.vendorAccountDropDownButtonTemplate = ['dropDownAddButton'];
                }
            },
            (error) => {
                this.showAPIErrorMessage(error);
            }
        );
    }

    private createCommunityVendor(): void {
        const baseParams: WFECreateCommunityVendorParametersDto = new WFECreateCommunityVendorParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.locationVendorId = this.selectedVendorPayee.vendorBranchAssociationId;

        this.loadingVisible = true;
        this.workflowEditorService.createCommunityVendor(baseParams).subscribe(
            (result: any) => {
                this.isBranchVendor = false;
                this.getVendorData(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private unlockWorkflow(): void {
        const baseParams: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;
        this.loadingVisible = true;
        this.workflowEditorService.closeWorkflowEditor(baseParams).subscribe(
            (result: StatusMessageResultDto) => {
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private transferWorkQueue(): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;

        this.loadingVisible = true;
        this.workflowEditorService.transferWorkQueue(baseParams).subscribe(
            (result: StatusMessageResultDto) => {
                if (result.isSuccessMessage) {
                    this.workflowActionSuccess(result.message, 'Transfer Work Queue');
                } else {
                    this.workflowMessageBox.showErrorMessage('Error', result.message);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private deleteWorkflow(deleteReason: string): void {
        const baseParams: WFEDeleteWorkflowParametersDto = new WFEDeleteWorkflowParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.workFlowEditor.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.deleteReason = deleteReason;

        this.loadingVisible = true;
        this.workflowEditorService.deleteWorkflow(baseParams).subscribe(
            (result: StatusMessageResultDto) => {
                if (result.isSuccessMessage) {
                    this.workflowActionSuccess(result.message, 'Delete AAP Workflow');
                } else {
                    this.workflowMessageBox.showErrorMessage('Error', result.message);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private replaceInvoiceSubmit(): void {
        this.loadingVisible = true;
        this.fileReader.readFile(this.invoiceAttachments[0]).subscribe(
            (result: AttachmentParameterDto) => {
                const baseParams: WFEReplaceInvoiceFileParametersDto = new WFEReplaceInvoiceFileParametersDto();
                baseParams.workflowId = this.workflowId;
                baseParams.workflowStatus = this.workFlowEditor.status;
                baseParams.communityId = this.selectedCommunity.communityId;
                baseParams.payeeCode = this.selectedPayeeCode;
                baseParams.invoiceDate = this.workFlowEditor.invoiceDate;
                baseParams.invoiceNumber = this.workFlowEditor.invoiceNumber;
                baseParams.fileName = result.fileName;
                baseParams.fileBase64string = result.attachmentBase64;

                this.workflowEditorService.replaceInvoice(baseParams).subscribe(
                    (sresult: StatusMessageResultDto) => {
                        if (sresult.isSuccessMessage) {
                            notify(sresult.message, 'success', MessageInterval.successMsgInterval);
                            this.getWorkflowInvoice(true);
                            this.showReplaceInvoiceFilePopUp = false;
                        } else {
                            this.workflowMessageBox.showErrorMessage('Error', sresult.message);
                        }
                        this.loadingVisible = false;
                    },
                    (error) => {
                        this.loadingVisible = false;
                        this.showAPIErrorMessage(error);
                    }
                );
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private insertDataGridNewRow(): void {
        const dataObj: WFEWorkflowItemRawDto = new WFEWorkflowItemRawDto();
        dataObj.sequence =
            this.workflowDataGrid.instance.totalCount() === -1 ? 1 : this.workflowDataGrid.instance.totalCount() + 1;
        dataObj.itemId = dataObj.sequence;
        dataObj.itemAmount = null;
        dataObj.description = null;
        dataObj.crudAction = 'add';
        this.wfItemsDataGridSource.push(dataObj);
        this.workflowDataGrid.instance.repaint();
    }

    private getVendorTemplate(clearCache = false): void {
        const baseParams: WFEVendorParametersDto = new WFEVendorParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.vendorCode = this.selectedVendorPayee.vendorCode;
        baseParams.clearCache = clearCache;

        this.workflowEditorService.getVendorLineTemplate(baseParams).subscribe(
            (result: WFEVendorTemplateResultDto) => {
                this.defaultLineItems = [];
                if (result.headerTemplate) {
                    this.workFlowEditor.description = result.headerTemplate.description
                        ? result.headerTemplate.description
                        : null;

                    this.workFlowEditor.bankAccountId = result.headerTemplate.bankAccountId;

                    this.workFlowEditor.specialHandlingId = this.checkIfHandlingExists(
                        result.headerTemplate.specialHandlingId
                    )
                        ? result.headerTemplate.specialHandlingId
                        : 0;
                }
                if (result.lineItemTemplates.length > 0) {
                    this.defaultLineItems = result.lineItemTemplates.filter((d) => !!d.enabled);
                    if (!result.hasAccountLevelTemplates && this.workFlowEditor.items.length === 0) {
                        this.setDefaultLineItems();
                    }
                } else if (
                    !result.hasAccountLevelTemplates &&
                    this.defaultLineItems.length === 0 &&
                    this.workFlowEditor.items.length === 0
                ) {
                    this.onAddNewRowToWorkflowGrid();
                }
            },
            (error) => {
                this.showAPIErrorMessage(error);
            }
        );
    }

    private getVendorAccountTemplate(clearCache = false): void {
        const baseParams: WFEVendorAccountParametersDto = new WFEVendorAccountParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.vendorCode = this.selectedVendorPayee.vendorCode;
        baseParams.vendorAccountId = this.selectedVendorAccountId;
        baseParams.clearCache = clearCache;

        this.workflowEditorService.getVendorAccountLineTemplate(baseParams).subscribe(
            (result: WFEVendorAccountTemplateResultDto) => {
                if (result.headerTemplate) {
                    this.workFlowEditor.description = result.headerTemplate.description
                        ? result.headerTemplate.description
                        : null;
                    this.workFlowEditor.specialHandlingId = this.checkIfHandlingExists(
                        result.headerTemplate.specialHandlingId
                    )
                        ? result.headerTemplate.specialHandlingId
                        : 0;
                    this.workFlowEditor.bankAccountId = result.headerTemplate.bankAccountId;
                }
                if (result.lineItemTemplates.length > 0) {
                    this.defaultLineItems = result.lineItemTemplates.filter((d) => !!d.enabled);
                    if (this.workFlowEditor.items.length === 0) {
                        this.setDefaultLineItems();
                    }
                } else {
                    this.getVendorTemplate();
                }
            },
            (error) => {
                this.showAPIErrorMessage(error);
                this.getVendorTemplate();
            }
        );
    }

    private sendWorkFlowToVendorEdit(): void {
        const baseParams: WFESendToVendorEditParametersDto = new WFESendToVendorEditParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.vendorCode = this.selectedVendorPayee.vendorCode;
        baseParams.invoiceDate = this.workFlowEditor.invoiceDate;
        baseParams.dueDate = this.workFlowEditor.dueDate;
        baseParams.invoiceNumber = this.workFlowEditor.invoiceNumber;
        baseParams.specialHandlingId = this.workFlowEditor.specialHandlingId;

        this.loadingVisible = true;
        this.workflowEditorService.sendWorkflowToVendorEdit(baseParams).subscribe(
            (result: StatusMessageResultDto) => {
                this.loadingVisible = false;
                this.workflowActionResultHandler(result);
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private getCommunityBillToAddress(clearCache = false): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.selectedCommunity.communityId;
        baseParams.workflowStatus = this.workFlowEditor.status;
        baseParams.clearCache = clearCache;

        this.workflowEditorService.getWorkflowCommunityBillAddress(baseParams).subscribe(
            (result: string) => {
                if (result) {
                    this.vendorBillToAddress = result + '<br/>';
                } else {
                    this.vendorBillToAddress = null;
                }
            },
            (error) => {
                this.showAPIErrorMessage(error);
            }
        );
    }

    private sendWorkflowToSupervisor(): void {
        this.loadingVisible = true;

        this.workflowEditorService.verifyAndSendWorkflowToSupervisor(this.workFlowEditor).subscribe(
            (result: StatusMessageResultDto) => {
                this.loadingVisible = false;
                this.workflowActionResultHandler(result);
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private verifyAndApproveWorkflow(): void {
        this.loadingVisible = true;

        this.workflowEditorService.verifyAndApproveWorkflow(this.workFlowEditor).subscribe(
            (result: StatusMessageResultDto) => {
                this.loadingVisible = false;
                this.workflowActionResultHandler(result);
            },
            (error) => {
                this.loadingVisible = false;
                this.showAPIErrorMessage(error);
            }
        );
    }

    private workflowActionResultHandler(result: StatusMessageResultDto): void {
        if (result.isSuccessMessage) {
            this.workflowActionSuccess(result.message);
        } else {
            this.workflowMessageBox.showErrorMessage('Error', result.message);
        }
    }

    private workflowActionSuccess(message: string, title: string = null) {
        this.workflowMessageBox.showSuccessMessage(title, message, () => {
            this.finalizeWorkflow();
        });
    }

    private workflowLoadError(message: string, title: string = null) {
        this.workflowMessageBox.showErrorMessage(title, message, () => {
            // If there was workflow load error then close/exit workflow
            this.closeOrExitWorkflowEditor(true);
        });
    }

    private showAPIErrorMessage(error: HttpErrorResponse, prefixMessage = ''): void {
        let message = error.error;

        if (!prefixMessage) {
            if (error.status === 404) {
                prefixMessage = '';
            }
        }

        if (error.status === 409) {
            // conflict
            // API usually returns this error status when a lock could not be obtained for workflow

            if (!message) {
                message = 'Could not lock work flow. Unable to retrieve information.';
            }

            this.workflowMessageBox.showWithFixedWidthAndIcon(
                CCMessageBoxIcon.error,
                'Error',
                prefixMessage + message,
                320
            );
        } else {
            this.errorService.displayError(error, prefixMessage);
        }
    }

    private showDataEntryErrorMessage(error: HttpErrorResponse, defaultError: string): void {
        let message = error.error;
        if (!message) {
            message = defaultError;
        }
        this.workflowLoadError(message);
        this.loadingVisible = false;
    }

    private finalizeWorkflow(): void {
        if (this.continuousProcessMode) {
            notify('Loading Next AAP Workflow...Please Wait', 'info', MessageInterval.infoMsgInterval);

            this.resetWorkflow();
            this.loadWorkflowEditor();
        } else if (!this.continuousProcessMode) {
            this.closeOrExitWorkflowEditor(true);
        }
    }

    private setSettingsMenu(): void {
        const settingsMenu = [
            {
                text: 'Show Invoice On Right Pane',
                visible: this.pdfViewerToLeft,
                onClick: () => {
                    this.pdfViewerToLeft = false;
                    this.splitterPositionChange.emit(this.pdfViewerToLeft);
                    this.setSettingsMenu();
                }
            },
            {
                text: 'Show Invoice On Left Pane',
                visible: !this.pdfViewerToLeft,
                onClick: () => {
                    this.pdfViewerToLeft = true;
                    this.splitterPositionChange.emit(this.pdfViewerToLeft);
                    this.setSettingsMenu();
                }
            },
            {
                text: 'Separate Window',
                visible: !this.separateWindow,
                onClick: () => {
                    this.separateWindow = true;
                    this.hideSplitter.emit(this.separateWindow);
                    this.onPdfClick();
                    this.changeSettingsMenu();
                }
            }
        ];

        this.settingsMenu = settingsMenu;
    }

    private changeSettingsMenu(): void {
        const settingsMenu = [
            {
                text: 'Show Side by Side Panel',
                onClick: () => {
                    this.separateWindow = false;
                    this.hideSplitter.emit(this.separateWindow);
                    this.setSettingsMenu();
                }
            }
        ];

        this.settingsMenu = settingsMenu;
    }

    private getSplitterContentPosition(): void {
        this.separateWindow = false;
        this.pdfViewerToLeft = JSON.parse(
            this.localStorageService.getItem(StorageKeyConstants.wfInvoiceViewerPosition, CCLocalStorageCategory.WFE) ||
                String(Constants.splitterPDFViewerToLeft)
        );
        this.setSettingsMenu();
        this.splitterPositionChange.emit(this.pdfViewerToLeft);
    }

    private loadWorkflowEditor(): void {
        this.workFlowEditor = new WFEWorkflowRawDto();
        this.setDefaultComponentValues();
        this.resetComponents();
        if (this.continuousProcessMode) {
            this.workflowId = null;
            this.getNextDataEntryEditor();
        } else {
            this.getDataEntryEditor();
        }
    }

    private prepareWorkFlowEditor(result: WFEWorkflowRawDto): void {
        if (!this.isPopupMode) {
            this.userService.updateSideNavigatorState(false);
            this.userService.updateTopMenuState(false);
        }

        this.workFlowEditor = result;
        this.configureWorkflow(); // update PayeeType to COMMUNITY from COMMUNITIES
        this.workflowId = this.workFlowEditor.workflowId;
        this.communityId = this.workFlowEditor.communityId;

        this.minimumInvoiceDate = moment(new Date()).subtract(1, 'year').toDate();

        this.setDefaultSpecialHandling();
        this.setVendorAccountEnableDisable();
        this.getCommunityData();
        this.getWorkflowInvoice(true);
        this.getWorkflowOptions();
    }

    private configureWorkflow(): void {
        // update PayeeType to COMMUNITY from COMMUNITIES
        if (!!this.workFlowEditor.payeeType && this.workFlowEditor.payeeType.toUpperCase() === 'COMMUNITIES') {
            this.workFlowEditor.payeeType = 'COMMUNITY';
        }
    }

    private closeOrExitWorkflowEditor(forceReload: boolean): void {
        if (this.isPopupMode) {
            this.closeAndNotifyParent(forceReload);
        } else {
            this.closeAndNavigate();
        }
    }

    private closeAndNotifyParent(forceReload: boolean): void {
        this.resetWorkflow();
        this.pdfSource = null;
        this.pdfLoader.emit(false);

        // Notify Parent
        this.workflowEditorService.updateSubjectRefreshWorkflow(forceReload);

        // Close Parent
        this.closeParentContainer.emit(forceReload);
    }

    private closeAndNavigate(): void {
        this.resetWorkflow();
        this.pdfSource = null;
        this.pdfLoader.emit(false);
        this.router.navigate(['/home']);
    }

    private closeAllPopup(): void {
        this.showHistoryPopUp = false;
        this.showWorkFlowNotesPopUp = false;
        this.showWorkFlowHistoryPopUp = false;
        this.showVendorNotesPopUp = false;
        this.showManageVendorAccountPopup = false;
        this.showAddEditVendorAccount = false;
        this.showDeleteWorkflowPopUp = false;

        this.showWorkFlowVendorInstructionsPopUp = false;
        this.showReplaceInvoiceFilePopUp = false;
    }
}
